import { useState } from 'react';
import { createClient, updateClient } from '../services/clientService';
import SelectRecherche from '../components/SelectRecherche';
import '../assets/styles/Parametrage.css';
import BarreProgression, { useProgression } from '../components/BarreProgression';
import RemplirParTexte from '../components/RemplirParTexte';

/**
 * Champs de la fiche, dans l'ordre de l'export Brevo. Les dates Brevo ne
 * se saisissent pas : elles viennent de l'import, ou restent vides.
 */
const CHAMPS = [
  { cle: 'prenom', label: 'Prénom' },
  { cle: 'nom', label: 'Nom' },
  { cle: 'email', label: 'Email', type: 'email', requis: true },
  { cle: 'sexe', label: 'Sexe', placeholder: 'Femme / Homme' },
  { cle: 'fonction', label: 'Fonction' },
  { cle: 'secteurActivite', label: "Secteur d'activité" },
  { cle: 'telephoneFixe', label: 'Téléphone fixe', type: 'tel' },
  { cle: 'whatsapp', label: 'WhatsApp', type: 'tel' },
  { cle: 'utilisateurAssigne', label: 'Utilisateur assigné' },
  { cle: 'contact', label: 'Contact (attribut Brevo)' },
  { cle: 'identifiantBrevo', label: 'Identifiant Brevo' },
];

const VIDE = Object.fromEntries(CHAMPS.map((c) => [c.cle, '']));

function clientToForm(client) {
  const form = { ...VIDE, abonne: '', bloque: '' };
  if (!client) return form;
  for (const { cle } of CHAMPS) form[cle] = client[cle] ?? '';
  form.abonne = client.abonne === null || client.abonne === undefined ? '' : String(client.abonne);
  form.bloque = client.bloque === null || client.bloque === undefined ? '' : String(client.bloque);
  return form;
}

/**
 * Modale de création / modification d'un client. `client` absent : création.
 * `onEnregistre(client)` reçoit la fiche renvoyée par le serveur.
 */
function ClientFormulaire({ client = null, onClose, onEnregistre }) {
  const [form, setForm] = useState(() => clientToForm(client));
  const [saving, setSaving] = useState(false);
  const progressionEnregistrement = useProgression(saving, { estimeeMs: 3000 });
  const [erreur, setErreur] = useState(null);

  const champ = (cle) => (e) => setForm((prev) => ({ ...prev, [cle]: e.target.value }));

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setErreur(null);
    const payload = {
      ...form,
      abonne: form.abonne === '' ? null : form.abonne === 'true',
      bloque: form.bloque === '' ? null : form.bloque === 'true',
    };
    try {
      const res = client ? await updateClient(client.id, payload) : await createClient(payload);
      onEnregistre(res.data);
    } catch (err) {
      setErreur(err.message || 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="param-modal-overlay" onClick={onClose}>
      <div className="param-modal" onClick={(e) => e.stopPropagation()}>
        <div className="param-modal-header">
          <h2 className="param-modal-title">{client ? 'Modifier la fiche' : 'Nouveau client'}</h2>
          <button type="button" className="param-modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <form className="param-form param-modal-body" onSubmit={handleSubmit}>
          {/* En tête de fiche, et collée là pendant le défilement : sous le dernier champ, on ne la voyait pas. */}
          {erreur && <div className="param-form-message param-form-message-error">{erreur}</div>}

          {/* À la création comme à la modification : le texte collé remplit la fiche. */}
          <RemplirParTexte
            variante="param"
            contexte="fiche d'un client (contact)"
            champs={CHAMPS.map((c) => ({
              cle: c.cle,
              libelle: c.label,
              type: c.type === 'email' ? 'email' : c.type === 'tel' ? 'telephone' : 'texte',
            }))}
            onValeurs={(v) => setForm((prev) => ({ ...prev, ...v }))}
          />

          {CHAMPS.map(({ cle, label, type = 'text', placeholder, requis }) => (
            <label className="param-field" key={cle}>
              <span className="param-label">{label}</span>
              <input
                type={type}
                className="param-input"
                value={form[cle]}
                placeholder={placeholder}
                onChange={champ(cle)}
                required={requis}
              />
            </label>
          ))}

          <div className="param-form-row">
            <label className="param-field param-field-grow">
              <span className="param-label">Abonné (Brevo)</span>
              <SelectRecherche className="param-select" value={form.abonne} onChange={champ('abonne')}>
                <option value="">Non renseigné</option>
                <option value="true">Oui</option>
                <option value="false">Non</option>
              </SelectRecherche>
            </label>
            <label className="param-field param-field-grow">
              <span className="param-label">Bloqué (Brevo)</span>
              <SelectRecherche className="param-select" value={form.bloque} onChange={champ('bloque')}>
                <option value="">Non renseigné</option>
                <option value="true">Oui</option>
                <option value="false">Non</option>
              </SelectRecherche>
            </label>
          </div>

          <BarreProgression valeur={progressionEnregistrement} libelle="Enregistrement du client" estimation />
          <div className="param-form-actions">
            <button type="button" className="param-btn" onClick={onClose} disabled={saving}>
              Annuler
            </button>
            <button type="submit" className="param-btn param-btn-primary" disabled={saving}>
              {saving ? 'Enregistrement' : client ? 'Enregistrer' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ClientFormulaire;
